import React from 'react';
import { useSelector } from 'react-redux';
import { useListings } from '../../../../hooks/useListings';
import { useListingsWithDistance } from '../../../../hooks/useListingsWithDistance';
import { insertAuthorDataIntoListing } from '../../../../util/listings';
import { resolveUserLatLng } from '../../../../util/user';
import { getVisibleSlidesByViewport } from '../../../../components/ListingCardsCarousel/helpers';
import { IconSpinner, ListingCardsCarouselWithMatchingScore } from '../../../../components';
import { findListingForOwner } from '../../../../components/ListingCard/helpers';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { selectOwnListingsEntities } from '../../../ManageListingsPage/ManageListingsPage.duck';
import config from '../../../../config';
import css from './SectionBestSuitsYouRiders.css';

const { listingTypeRider, minMatchingRate } = config;

export default ({ currentUser }) => {
    const [isMobile, , computing, viewport] = useIsMobile(768);
    const {
        attributes: {
            profile: {
                publicData: { mainHorseId },
            },
        },
    } = currentUser;
    const userLocationData = resolveUserLatLng(currentUser);

    const [riderListings, riderListingsLoading, included] = useListings({
        addDataToMarketplace: true,
        params: {
            per_page: 20,
            origin: userLocationData,
            pub_type: listingTypeRider,
            include: ['author', 'author.profileImage', 'images'],
            'fields.user': [
                'profile.displayName',
                'profile.abbreviatedName',
                'profile.bio',
                'profile.publicData.country',
            ],
            'fields.listing': ['title', 'state', 'geolocation', 'price', 'publicData', 'createdAt'],
            'fields.image': [
                'variants.landscape-crop',
                'variants.landscape-crop2x',
                'variants.square-small',
            ],
        },
    });

    /**
     * the current horseowner's own listings - fetched once per session by
     * middlewares/currentUser.js (queryOwnListings), read here rather than
     * re-fetched (see ManageListingsPage.duck.js:selectOwnListingsEntities).
     * Narrowed to the main one client-side when known, same rule
     * ListingCard's mapStateToProps already applies.
     */
    const ownListings = useSelector(selectOwnListingsEntities);
    const ownListingsLoading = useSelector(state => state.ManageListingsPage.queryInProgress);
    const horseListings = mainHorseId
        ? ownListings.filter(findListingForOwner(mainHorseId))
        : ownListings;

    const riderListingsDataAvailable = Array.isArray(riderListings) && riderListings.length > 0;
    const horseListingDataAvailable = Array.isArray(horseListings) && horseListings.length > 0;
    const dataAvailable = viewport && riderListingsDataAvailable && horseListingDataAvailable;
    const horseListingId = dataAvailable ? horseListings[0].id.uuid : null;
    const includedDataAvailable = !!Array.isArray(included) && included.length > 0;
    const isLoading = riderListingsLoading || ownListingsLoading;

    const listingsWithDistance = useListingsWithDistance(
        !isLoading && dataAvailable ? riderListings : [],
        userLocationData
    );

    if (isLoading || computing) return <IconSpinner />;

    const applyAuthDataInserting = listings =>
        includedDataAvailable
            ? listings.map(listing => insertAuthorDataIntoListing(listing, included))
            : listings;

    const carouselHolderClass =
        dataAvailable && listingsWithDistance.length === 2 ? css.carouselHolderTwoColumn : null;

    return dataAvailable ? (
        <section className={css.root}>
            <ListingCardsCarouselWithMatchingScore
                isMobile={isMobile}
                listings={applyAuthDataInserting(listingsWithDistance)}
                riderListing={{ id: { uuid: horseListingId } }}
                minMatchingRate={minMatchingRate}
                matchRiderListing={false}
                sliderClassName={css.sliderRoot}
                isLoggedIn
                hideButtonsOnEndStart
                sliderVisibleSlides={getVisibleSlidesByViewport(viewport).visibleSlidesByViewport}
                carouselHolderClassName={carouselHolderClass}
                header={
                    <>
                        <h2 className={css.heading}>Beste Treffer</h2>
                        <p>Bei diesen Pferdemenschen hast Du die besten Chancen.</p>
                    </>
                }
            />
        </section>
    ) : null;
};
